import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import dayjs from "dayjs";
import {
  ShieldCheck, Briefcase, Users, EyeOff, Power, Trash2, Loader2,
  FileText, FileDown, Upload, X, Building2, Ban, KeyRound, Copy,
} from "lucide-react";
import Navbar from "../components/Navbar";
import StatusBadge from "../components/StatusBadge";
import { useLanguage } from "../i18n/LanguageContext";
import api, { formatApiError } from "../lib/api";

const inputClass = "w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400";
const labelClass = "text-xs font-bold uppercase tracking-wider text-slate-600";

export default function AdminDashboard() {
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState("jobs");
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [examples, setExamples] = useState([]);
  const [users, setUsers] = useState([]);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";
  const locale = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US";

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, j, e, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/jobs"),
        api.get("/cv-examples"),
        api.get("/admin/users"),
      ]);
      setStats(s.data);
      setJobs(j.data);
      setExamples(e.data);
      setUsers(u.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll().catch(() => {}); }, [fetchAll]);

  const toggleJob = async (job) => {
    const next = job.status === "active" ? "inactive" : "active";
    await api.patch(`/jobs/${job.job_id}/status`, { status: next });
    toast.success(t("company.statusUpdated"));
    fetchAll();
  };

  const deleteJob = async (job) => {
    if (!window.confirm(t("company.deleteConfirm"))) return;
    await api.delete(`/jobs/${job.job_id}`);
    toast.success(t("company.jobDeleted"));
    fetchAll();
  };

  const uploadExample = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      await api.post("/admin/cv-examples", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("admin.exampleAdded"));
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err, t("candidate.uploadError")));
    } finally {
      setUploading(false);
    }
  };

  const deleteExample = async (ex) => {
    if (!window.confirm(t("admin.deleteExampleConfirm"))) return;
    await api.delete(`/admin/cv-examples/${ex.file_id}`);
    toast.success(t("admin.exampleDeleted"));
    fetchAll();
  };

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/block`, { blocked: !u.blocked });
      toast.success(u.blocked ? t("admin.userUnblocked") : t("admin.userBlocked"));
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err, t("common.error")));
    }
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#";
    setNewPassword(Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join(""));
  };

  const submitResetPassword = async () => {
    if (newPassword.length < 6) return;
    try {
      await api.post(`/admin/users/${resetTarget.user_id}/reset-password`, { new_password: newPassword });
      setResetDone(true);
      toast.success(t("admin.passwordReset"));
    } catch (err) {
      toast.error(formatApiError(err, t("common.error")));
    }
  };

  const TABS = [
    { key: "jobs", label: t("admin.jobsTab"), icon: Briefcase },
    { key: "examples", label: t("admin.examplesTab"), icon: FileText },
    { key: "users", label: t("admin.usersTab"), icon: Users },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-md border-2 border-slate-900 bg-yellow-300">
            <ShieldCheck className="h-6 w-6 text-slate-900" strokeWidth={2} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">{t("admin.dashboard")}</h1>
            <p className="text-sm text-slate-500">Global Line Services</p>
          </div>
        </div>

        {stats && (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-md border-2 border-slate-900 bg-white p-5" data-testid="admin-kpi-jobs">
              <p className="font-heading text-3xl font-bold text-slate-900">{stats.jobs}</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Briefcase className="h-4 w-4" /> {t("company.totalJobs")}</p>
            </div>
            <div className="rounded-md border-2 border-slate-900 bg-white p-5" data-testid="admin-kpi-hidden">
              <p className="font-heading text-3xl font-bold text-slate-900">{stats.hidden_jobs}</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><EyeOff className="h-4 w-4" /> {t("admin.hiddenJobs")}</p>
            </div>
            <div className="rounded-md border-2 border-slate-900 bg-white p-5" data-testid="admin-kpi-companies">
              <p className="font-heading text-3xl font-bold text-slate-900">{stats.companies}</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Building2 className="h-4 w-4" /> {t("landing.statsCompanies")}</p>
            </div>
            <div className="rounded-md border-2 border-slate-900 bg-white p-5" data-testid="admin-kpi-candidates">
              <p className="font-heading text-3xl font-bold text-slate-900">{stats.candidates}</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Users className="h-4 w-4" /> {t("landing.statsCandidates")}</p>
            </div>
          </div>
        )}

        <div className="mt-8 flex gap-2 border-b-2 border-slate-200" data-testid="admin-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.key} onClick={() => setTab(tb.key)} data-testid={`admin-tab-${tb.key}`}
              className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                tab === tb.key ? "border-sky-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <tb.icon className="h-4 w-4" strokeWidth={2} /> {tb.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
        ) : (
          <div className="mt-8">
            {tab === "jobs" && (
              jobs.length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-16 text-center text-sm text-slate-500" data-testid="admin-no-jobs">{t("company.noJobs")}</div>
              ) : (
                <div className="overflow-x-auto rounded-md border-2 border-slate-900 bg-white" data-testid="admin-jobs-table">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b-2 border-slate-900 bg-sky-50">
                      <tr>
                        <th className="px-5 py-3 font-heading font-bold">{t("company.jobTitle")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("admin.company")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("company.location")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("company.date")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("status.active")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("company.totalApplications")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("company.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job) => (
                        <tr key={job.job_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`admin-job-row-${job.job_id}`}>
                          <td className="px-5 py-3.5 font-semibold text-slate-900">{job.title}</td>
                          <td className="px-5 py-3.5 text-slate-600">{job.company_name}</td>
                          <td className="px-5 py-3.5 text-slate-600">{job.location}</td>
                          <td className="px-5 py-3.5 text-slate-600">{dayjs(job.created_at).locale(locale).format("DD/MM/YYYY")}</td>
                          <td className="px-5 py-3.5"><StatusBadge status={job.status} t={t} /></td>
                          <td className="px-5 py-3.5 text-slate-600">{job.applicants_count}</td>
                          <td className="px-5 py-3.5">
                            <div className="flex gap-2">
                              <button onClick={() => toggleJob(job)} data-testid={`admin-toggle-job-${job.job_id}`}
                                title={job.status === "active" ? t("admin.hide") : t("admin.show")}
                                className={`rounded-md border border-slate-900 p-1.5 transition-transform hover:-translate-y-0.5 ${job.status === "active" ? "bg-yellow-100" : "bg-green-100"}`}>
                                <Power className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => deleteJob(job)} data-testid={`admin-delete-job-${job.job_id}`} title={t("company.delete")}
                                className="rounded-md border border-slate-900 bg-red-100 p-1.5 transition-transform hover:-translate-y-0.5">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {tab === "users" && (
              users.length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-16 text-center text-sm text-slate-500" data-testid="admin-no-users">{t("admin.noUsers")}</div>
              ) : (
                <div className="overflow-x-auto rounded-md border-2 border-slate-900 bg-white" data-testid="admin-users-table">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b-2 border-slate-900 bg-sky-50">
                      <tr>
                        <th className="px-5 py-3 font-heading font-bold">{t("admin.user")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("admin.role")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("company.date")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("status.active")}</th>
                        <th className="px-5 py-3 font-heading font-bold">{t("company.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`admin-user-row-${u.user_id}`}>
                          <td className="px-5 py-3.5">
                            <p className="font-semibold text-slate-900">{u.name}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`rounded-full border border-slate-900 px-2.5 py-0.5 text-xs font-semibold ${
                              u.role === "admin" ? "bg-yellow-100 text-yellow-900" : u.role === "company" ? "bg-sky-100 text-sky-900" : "bg-green-100 text-green-900"
                            }`}>{u.role}</span>
                          </td>
                          <td className="px-5 py-3.5 text-slate-600">{dayjs(u.created_at).locale(locale).format("DD/MM/YYYY")}</td>
                          <td className="px-5 py-3.5">
                            <span data-testid={`user-status-${u.user_id}`} className={`inline-flex items-center rounded-full border border-slate-900 px-2.5 py-0.5 text-xs font-semibold ${u.blocked ? "bg-red-100 text-red-900" : "bg-green-100 text-green-900"}`}>
                              {u.blocked ? t("admin.blocked") : t("admin.activeUser")}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            {u.role !== "admin" && (
                              <div className="flex gap-2">
                                <button onClick={() => toggleBlock(u)} data-testid={`block-user-${u.user_id}`}
                                  title={u.blocked ? t("admin.unblock") : t("admin.block")}
                                  className={`rounded-md border border-slate-900 p-1.5 transition-transform hover:-translate-y-0.5 ${u.blocked ? "bg-green-100" : "bg-red-100"}`}>
                                  <Ban className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => { setResetTarget(u); setNewPassword(""); setResetDone(false); }} data-testid={`reset-password-${u.user_id}`}
                                  title={t("admin.resetPassword")}
                                  className="rounded-md border border-slate-900 bg-sky-100 p-1.5 transition-transform hover:-translate-y-0.5">
                                  <KeyRound className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {tab === "examples" && (
              <div>
                <div className="rounded-md border-2 border-slate-900 bg-white p-6" data-testid="cv-example-upload-form">
                  <h2 className="font-heading text-base font-bold text-slate-900">{t("admin.addExample")}</h2>
                  <div className="mt-4 flex flex-wrap items-end gap-3">
                    <div className="min-w-56 flex-1">
                      <label className={labelClass}>{t("admin.exampleTitle")}</label>
                      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("admin.exampleTitlePlaceholder")} data-testid="cv-example-title-input" className={`${inputClass} mt-1.5`} />
                    </div>
                    <div>
                      <label className={labelClass}>{t("admin.file")}</label>
                      <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" data-testid="cv-example-file-input"
                        className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:rounded-full file:border-2 file:border-slate-900 file:bg-white file:px-4 file:py-2 file:text-xs file:font-bold file:text-slate-900 hover:file:bg-sky-100" />
                    </div>
                    <button onClick={uploadExample} disabled={uploading} data-testid="cv-example-upload-button"
                      className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-6 py-2.5 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 disabled:opacity-60">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" strokeWidth={2.5} />} {t("admin.upload")}
                    </button>
                  </div>
                </div>

                <div className="mt-6">
                  {examples.length === 0 ? (
                    <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-16 text-center text-sm text-slate-500" data-testid="admin-no-examples">{t("admin.noExamples")}</div>
                  ) : (
                    <div className="space-y-4" data-testid="admin-examples-list">
                      {examples.map((ex) => (
                        <div key={ex.file_id} className="flex flex-wrap items-center justify-between gap-4 rounded-md border-2 border-slate-900 bg-white p-5" data-testid={`admin-example-${ex.file_id}`}>
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-md border-2 border-slate-900 bg-yellow-100">
                              <FileText className="h-5 w-5 text-slate-900" strokeWidth={2} />
                            </span>
                            <div>
                              <p className="font-heading text-base font-bold text-slate-900">{ex.title}</p>
                              <p className="text-xs text-slate-500">{ex.original_filename} · {dayjs(ex.created_at).locale(locale).format("DD/MM/YYYY")}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <a href={`${apiBase}/files/${ex.storage_path}`} target="_blank" rel="noreferrer" data-testid={`admin-download-example-${ex.file_id}`}
                              className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-4 py-1.5 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-100">
                              <FileDown className="h-4 w-4" strokeWidth={2} /> {t("candidate.download")}
                            </a>
                            <button onClick={() => deleteExample(ex)} data-testid={`admin-delete-example-${ex.file_id}`}
                              className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-red-100 px-4 py-1.5 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5">
                              <Trash2 className="h-4 w-4" strokeWidth={2} /> {t("company.delete")}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {resetTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" data-testid="reset-password-modal">
          <div className="w-full max-w-md rounded-md border-2 border-slate-900 bg-white p-8 shadow-[8px_8px_0px_0px_rgba(15,23,42,1)]">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-slate-900">{t("admin.resetPassword")}</h2>
              <button onClick={() => setResetTarget(null)} data-testid="reset-password-close" className="rounded-md border-2 border-slate-900 p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-sm text-slate-500">{resetTarget.name} — {resetTarget.email}</p>
            <div className="mt-5">
              <label className={labelClass}>{t("admin.newPassword")}</label>
              <div className="mt-1.5 flex gap-2">
                <input
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={6}
                  data-testid="new-password-input" className={inputClass} disabled={resetDone}
                />
                <button type="button" onClick={generatePassword} data-testid="generate-password-button" disabled={resetDone}
                  className="shrink-0 rounded-full border-2 border-slate-900 bg-white px-4 text-xs font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-100 disabled:opacity-60">
                  {t("admin.generate")}
                </button>
              </div>
            </div>
            {resetDone ? (
              <div className="mt-5 rounded-md border-2 border-green-600 bg-green-50 p-4" data-testid="reset-password-success">
                <p className="text-xs font-semibold text-green-900">{t("admin.passwordResetHint")}</p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 rounded border border-green-600 bg-white px-3 py-2 text-sm font-bold text-slate-900">{newPassword}</code>
                  <button onClick={() => navigator.clipboard.writeText(newPassword)} data-testid="copy-password-button"
                    className="rounded-md border-2 border-slate-900 bg-white p-2 transition-transform hover:-translate-y-0.5" title="Copy">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <button onClick={() => setResetTarget(null)} data-testid="reset-password-done"
                  className="mt-4 w-full rounded-full border-2 border-slate-900 bg-sky-400 py-2 text-sm font-bold text-slate-900">OK</button>
              </div>
            ) : (
              <div className="mt-6 flex gap-3">
                <button onClick={() => setResetTarget(null)} data-testid="reset-password-cancel"
                  className="flex-1 rounded-full border-2 border-slate-900 bg-white py-2.5 text-sm font-bold text-slate-900">{t("company.cancel")}</button>
                <button onClick={submitResetPassword} disabled={newPassword.length < 6} data-testid="reset-password-submit"
                  className="flex-1 rounded-full border-2 border-slate-900 bg-sky-400 py-2.5 text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 disabled:opacity-60">
                  {t("admin.resetPassword")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
